import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, Wallet, Users, Gift, Shield, LogOut, User, MessageCircle } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_link-builder-22/artifacts/o4eavll4_a942b2bb-6513-4b93-b220-bd15a0be07b2.jpg';
const WHATSAPP_NUMBER = '34633921162';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/wallet', icon: Wallet, label: 'Monedero' },
  { to: '/referral', icon: Gift, label: 'Referidos' },
  { to: '/profile', icon: User, label: 'Perfil' },
];

const adminItems = [
  { to: '/admin', icon: Shield, label: 'Admin' },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid="app-layout">
      {/* Mobile Top Bar */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 bg-diavel-card border-b border-white/5 sticky top-0 z-50 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Diavel" className="w-8 h-8 object-contain" />
          <span className="font-heading font-bold text-sm uppercase tracking-wider">INFERNO</span>
        </div>
        <button onClick={handleLogout} data-testid="mobile-logout-btn" className="text-zinc-500 hover:text-white transition-colors">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex flex-col w-64 min-h-screen bg-diavel-card border-r border-white/5 sticky top-0" data-testid="sidebar">
        <div className="p-6 border-b border-white/5">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Diavel" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="font-heading font-bold text-sm uppercase tracking-wider text-white">INFERNO</h1>
              <p className="font-mono text-[10px] tracking-wider uppercase text-zinc-600">Diavel Tattoo</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => (
            <NavLink
              key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all duration-300 ${
                  isActive ? 'bg-red-900/20 text-diavel-red border-l-2 border-diavel-red' : 'text-zinc-500 hover:text-white hover:bg-white/5 border-l-2 border-transparent'
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
          {isAdmin && adminItems.map(item => (
            <NavLink
              key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all duration-300 ${
                  isActive ? 'bg-red-900/20 text-diavel-red border-l-2 border-diavel-red' : 'text-zinc-500 hover:text-white hover:bg-white/5 border-l-2 border-transparent'
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-4 py-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs font-bold text-white overflow-hidden">
              {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-zinc-600 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout} data-testid="logout-btn"
            className="flex items-center gap-3 px-4 py-3 text-sm text-zinc-500 hover:text-red-400 transition-all duration-300 w-full"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-h-screen relative z-10">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-diavel-card border-t border-white/5 flex z-50 backdrop-blur-xl">
        {navItems.map(item => (
          <NavLink
            key={item.to} to={item.to} data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center py-3 text-xs transition-all duration-300 ${
                isActive ? 'text-diavel-red' : 'text-zinc-600'
              }`
            }
          >
            <item.icon className="w-5 h-5 mb-1" />
            {item.label}
          </NavLink>
        ))}
        {isAdmin && (
          <NavLink
            to="/admin" data-testid="mobile-nav-admin"
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center py-3 text-xs transition-all duration-300 ${
                isActive ? 'text-diavel-red' : 'text-zinc-600'
              }`
            }
          >
            <Shield className="w-5 h-5 mb-1" />
            Admin
          </NavLink>
        )}
      </div>

      {/* WhatsApp floating button */}
      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hola! Soy del INFERNO de Diavel Tattoo')}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-btn"
        className="fixed bottom-20 md:bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:shadow-[0_0_20px_rgba(37,211,102,0.4)] transition-all duration-300 hover:scale-110 group"
        title="Hablar por WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
        <span className="absolute right-full mr-3 bg-white text-black text-xs font-semibold py-2 px-3 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-md pointer-events-none">
          ¿Hablamos?
        </span>
      </a>
    </div>
  );
}
